export interface HTTPValidationError {
    detail: ValidationError[] | string
}

// Admin-only схема архитектуры ядра.  Детальный (конфиденциальный) контент
// приходит с защищённого эндпоинта /architecture/diagram, не из бандла.
export interface ArchitectureDiagram {
    hero: { kicker: string; title: string; sub: string; tag: string }
    before: {
        title: string
        desc: string
        robots: string[]
        items: string[]
        note: string
    }
    after: {
        title: string
        engineLabel: string
        engineTitle: string
        engineItems: string[]
        portCommand: { name: string; dir: string }
        portFeed: { name: string; dir: string }
        adapters: { name: string; role: string; ready: boolean }[]
    }
    domain: {
        title: string
        desc: string
        types: { name: string; note: string }[]
    }
    diffs: {
        title: string
        desc: string
        items: { title: string; desc: string }[]
    }
    phases: {
        title: string
        desc: string
        items: {
            num: string
            title: string
            desc: string
            status: 'done' | 'next' | 'todo'
        }[]
    }
    status: { title: string; cards: { val: string; lbl: string }[] }
    footer: string[]
}

// Admin-only книжный нарратив (очищенный, без механики алгоритма).
// Контент приходит с защищённого эндпоинта /book/draft, не из бандла.
export interface BookDraft {
    content: string
    lang: string       // фактически отданный язык
    requested: string  // запрошенный язык (может отличаться, если перевода ещё нет)
}

export interface ValidationError {
    loc: string[]
    msg: string
    type: string
}

export interface TronNonceBodyModel {
    address: string
}

export interface TronNonceModel {
    nonce: string
    isNew: boolean
}

export interface TronVerifyModel {
    nonce: string
    sign: string
    address: string
    // id реферера из ссылки ?ref=<id> — авто-привязка при регистрации.
    referrerId?: number
}

export interface AccountBaseModel {
    id: number
    createDate: number
    active: boolean
    isAdmin: boolean
    isPartner: boolean
    deposit: number
    locked: number
    /** Комиссия за вывод с комиссионного депозита, RCC (удерживается из суммы). */
    withdrawFee?: number
    // Бесплатный доступ к алгоритму до этого момента (ms, UTC). null = нет.
    freeAccessUntil?: number | null
    tronAddress?: string | null
    username?: string | null
    nickname?: string | null
    nicknameChangedAt?: number | null
    email?: string | null
    phone?: number | null
    firstName?: string | null
    lastName?: string | null
}

export interface AccountModel extends AccountBaseModel {
    referrerId?: number | null
}

export interface PartnerClientExchangeModel {
    exchange: 'binance' | 'bybit' | 'okx' | 'rithmic'
    accountId: number
    connected: boolean
    running: boolean
}

export interface PartnerClientModel {
    id: number
    nickname?: string | null
    username?: string | null
    tronAddress?: string | null
    createDate: number
    exchanges: PartnerClientExchangeModel[]
}

export interface PartnerClientTradeModel {
    clientId: number
    nickname?: string | null
    username?: string | null
    exchange: string
    connected: boolean
    running: boolean
    symbol?: string | null
}

export interface AccountWithMarketsModel extends AccountBaseModel {
    binance?: BinanceDataModel
}

export interface AccountCreateModel {
    username: string
    password: string
    token: string
}

export interface AccountUpdateModel {
    tronAddress?: string
    active?: boolean
    isAdmin?: boolean
}

export interface AccountSearchModel {
    isRun?: boolean
}

export interface AccountDepositModel {
    deposit: number
}

export interface LoginModel {
    username: string
    password: string
}

export interface BinanceCreateModel {
    secretKey: string
    accessKey: string
}

export interface BinanceUpdateModel {
    secretKey?: string
    accessKey?: string
    isWithdraw?: boolean
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface RithmicCreateModel {
    secretKey: string
    accessKey: string
    system: RithmicSystemsTypes
}

export interface RithmicUpdateDto {
    secretKey?: string
    accessKey?: string
    system?: RithmicSystemsTypes
    commissionRatio?: number
    isBeStopped?: boolean
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface BybitCreateDto {
    secretKey: string
    accessKey: string
}

export interface BybitUpdateDto {
    secretKey?: string
    accessKey?: string
    isWithdraw?: boolean
    isBeStopped?: boolean         // ручная разблокировка после auto-BE-stop
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface BinanceDataModel {
    id: number
    createDate: number
    secretKey: string
    accessKey: string
    isWithdraw: boolean
    isBeStopped: boolean
    robotId?: string
    symbolId?: number | null
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
    /** Лот и тейк, которые клиент поставил сам (пусто — как у инструмента). */
    clientLot?: number | null
    clientTakeProfit?: number | null
}

export interface RithmicDataModel {
    id: number
    createDate: number
    secretKey: string
    accessKey: string
    commissionRatio: number
    system: RithmicSystemsTypes
    isBeStopped: boolean
    robotId?: string | null
    symbolId?: number | null
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
    /** Лот и тейк, которые клиент поставил сам (пусто — как у инструмента). */
    clientLot?: number | null
    clientTakeProfit?: number | null
}

export interface BybitDataModel {
    id: number
    account: AccountBaseModel
    createDate: number
    isWithdraw: boolean
    isBeStopped: boolean
    secretKey?: string
    accessKey?: string
    robotId?: string
    symbolId?: number
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
    /** Лот и тейк, которые клиент поставил сам (пусто — как у инструмента). */
    clientLot?: number | null
    clientTakeProfit?: number | null
}

export interface BybitDataSearchDto extends BybitDataModel {
    account: AccountModel
}

/**
 * Bitget-аккаунт.  Зеркало Bybit + passphrase (третий секрет, как OKX);
 * на сервере passphrase hidden-маскируется в DataModel.
 */
export interface BitgetCreateDto {
    secretKey: string
    accessKey: string
    passphrase: string
}

export interface BitgetUpdateDto {
    secretKey?: string
    accessKey?: string
    passphrase?: string
    isWithdraw?: boolean
    isBeStopped?: boolean
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface BitgetDataModel {
    id: number
    account: AccountBaseModel
    createDate: number
    isWithdraw: boolean
    isBeStopped: boolean
    secretKey?: string
    accessKey?: string
    passphrase?: string
    robotId?: string
    symbolId?: number
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
    /** Лот и тейк, которые клиент поставил сам (пусто — как у инструмента). */
    clientLot?: number | null
    clientTakeProfit?: number | null
}

export interface BitgetDataSearchDto extends BitgetDataModel {
    account: AccountModel
}

export interface BitgetStartDto {
    symbolId: number
}

export interface BitgetHardStopAllDto {
    ids?: number[]
}

export interface BitgetSearchDto {
    isRun: boolean
}

export interface BitgetSymbolBaseDto {
    name: string
    symbol: string
    stepSize: number
    defaultPositionSize: number
    defaultMaxLots: number
    fullDrawdown: number
    stairVersion: string
    classicMaxSteps: number
    minBalance: number
    takeProfitRatio: number
    feedCount: number
    onlyAdmin: boolean
    priceStep: number
    quantityStep: number
    leverage?: number
    hedgingEnable: boolean
    candleType?: string
    candleVolume?: number
    interval?: string
    hedgeV2Enable?: boolean
    hedgeV2UseLevels?: boolean
    stairTrendThrottle?: boolean
    hedgeV2BandFirstOpen?: boolean
    hedgeV2FlipEnable?: boolean
    /** Границы для лота и тейка клиента; пусто — тейк ½…×5, лот до ×10. */
    clientTpMin?: number | null
    clientTpMax?: number | null
    clientLotMax?: number | null
}

export interface BitgetSymbolDto extends BitgetSymbolBaseDto {
    id: number
}

export interface BitgetSymbolForSelectDto {
    id: number
    name: string
    symbol: string
    onlyAdmin: boolean
    minBalance: number
}

/**
 * OKX-аккаунт.  Отличия от Bybit:
 *   1. passphrase — третий секрет (обязателен при создании).
 *   2. На сервере поле hidden-маскируется в DataModel (первые/последние 4 char).
 */
export interface OkxCreateDto {
    secretKey: string
    accessKey: string
    passphrase: string
}

export interface OkxUpdateDto {
    secretKey?: string
    accessKey?: string
    passphrase?: string
    isWithdraw?: boolean
    isBeStopped?: boolean
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface OkxDataModel {
    id: number
    account: AccountBaseModel
    createDate: number
    isWithdraw: boolean
    isBeStopped: boolean
    secretKey?: string
    accessKey?: string
    passphrase?: string
    robotId?: string
    symbolId?: number
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
    /** Лот и тейк, которые клиент поставил сам (пусто — как у инструмента). */
    clientLot?: number | null
    clientTakeProfit?: number | null
}

export interface OkxDataSearchDto extends OkxDataModel {
    account: AccountModel
}

export interface OkxStartDto {
    symbolId: number
}

export interface OkxHardStopAllDto {
    ids?: number[]
}

export interface OkxSearchDto {
    isRun: boolean
}

export interface OkxSymbolBaseDto {
    name: string
    symbol: string  // OKX instId, например "BTC-USDT-SWAP"
    stepSize: number
    defaultPositionSize: number
    defaultMaxLots: number
    // Глубина просадки (%), на которой доливки набирают позицию до MAX.
    fullDrawdown: number
    // Версия алгоритма доливки: safe | gap_protect | fibonacci | classic.
    stairVersion: string
    // Лимит размера одной доливки для classic: не больше MAX_LOTS/N.
    classicMaxSteps: number
    minBalance: number
    takeProfitRatio: number
    feedCount: number
    onlyAdmin: boolean
    priceStep: number
    quantityStep: number
    /** OKX-специфика: размер 1 контракта в базовой валюте (для USDT-SWAP обычно 1 либо 0.01). */
    ctVal: number
    hedgingEnable: boolean
    candleType?: string
    candleVolume?: number
    interval?: string
    hedgeV2Enable?: boolean
    hedgeV2UseLevels?: boolean
    /** Границы для лота и тейка клиента; пусто — тейк ½…×5, лот до ×10. */
    clientTpMin?: number | null
    clientTpMax?: number | null
    clientLotMax?: number | null
}

export interface OkxSymbolDto extends OkxSymbolBaseDto {
    id: number
}

export interface OkxSymbolForSelectDto {
    id: number
    name: string
    symbol: string
    onlyAdmin: boolean
    minBalance: number
}

export interface RithmicDataSearchDto extends RithmicDataModel {
    account: AccountModel
}

export interface BaseSearchResponseModel<T extends object> {
    items: T[] | []
    total: number
}

export enum MarketType {
    BINANCE = 'BINANCE',
    RITHMIC = 'RITHMIC',
    BYBIT = 'BYBIT',
    BITGET = 'BITGET',
    OKX = 'OKX',
}

export interface DealsRequestModel {
    start_date: number
    end_date: number
    user_id?: number
}

export interface DealsResponseModel {
    id: number
    create_date: number
    symbol: string
    size: number
    entry_price: number
    exit_price: number
    market_commission: number
    commission: number
    profit: number
    status: CommissionStatusTypes
    withdraw_date: number
}

export interface LiveTradingResponseModel {
    create_date: number
    symbol: string
    size: number
    entry_price: number
    exit_price: number
    market_commission: number
    // RCC (платформенная) комиссия — отдаёт Bybit live-trading эндпоинт.
    // Опционально ради backward-compat с Binance-версией без этого поля.
    commission?: number
    profit: number
}

export enum CommissionStatusTypes {
    TAKEN = 'TAKEN',
    TRANSFER = 'TRANSFER',
    NOT_PROCESSED = 'NOT_PROCESSED',
}

export enum RithmicSystemsTypes {
    RITHMIC_01 = 'Rithmic 01',
    RITHMIC_PAPER_TRADING = 'Rithmic Paper Trading',
}

export interface StatisticRequestModel {
    user_id?: number
}

export interface StatisticResponseModel {
    today: number
    month: number
    year: number
    // Komissions for the same period.  RCC = platform, USDT = exchange.
    today_commission_rcc?: number
    month_commission_rcc?: number
    year_commission_rcc?: number
    today_commission_usdt?: number
    month_commission_usdt?: number
    year_commission_usdt?: number
}

export interface PeriodStatisticResponseModel {
    amount: number
    commission_rcc?: number
    commission_usdt?: number
}

export interface MarketDataStartRequestModel {
    symbolId: number
}

export interface RithmicStartDto {
    symbolId: number
}

export interface BybitStartDto {
    symbolId: number
}

export interface BybitHardStopAllDto {
    ids?: number[]
}

/** Данные калькулятора робота (живая цена, комиссии, параметры инструмента). */
/** Один день реальной торговли (UTC): сделок, контрактов/монет, прибыли. */
export interface HistoryDayModel {
    day: string
    deals: number
    volume: number
    profit: number
}

export interface CryptoCalculatorDataModel {
    symbolId: number
    symbol: string
    name: string
    price: number | null
    takerFee: number
    makerFee: number
    feeSource: 'account' | 'default'
    maintenanceMarginRate: number
    /** Ступени маржи биржи: объём позиции до (USDT) → ставка, %. */
    riskTiers?: { limit: number; mmr: number }[]
    leverage: number
    takeProfit: number
    positionSize: number
    maxLots: number
    fullDrawdown: number
    minBalance: number
    quantityStep: number
    balance: number | null
    /** Худший ход цены против позиции за 7/14/30 дней в истории, %. */
    extremes: {
        days: number
        down7: number
        up7: number
        down14: number
        up14: number
        down30: number
        up30: number
    } | null
    /** Реальная торговля робота по инструменту за days дней (все счета). */
    history: {
        days: number
        accounts: number
        deals: number
        accountDays: number
        tradesPerDay: number
        volumePerLotDay: number
        netPerLotDay: number
        /** Rithmic: тейк (пунктов), с которым робот реально торговал в этом окне. */
        takeProfit?: number | null
        /** Факты за период — реальные лоты счетов (не на 1 лот). */
        totalVolume: number
        totalProfit: number
        fromMs: number | null
        toMs: number | null
        daily: HistoryDayModel[]
    } | null
    /** Шарп, просадка… по ежедневному капиталу реальных счетов (окно — год). */
    riskMetrics: RiskMetricsModel | null
    /** «Мой счёт»: только сделки счёта, который открыл калькулятор. */
    ownHistory?: CryptoCalculatorDataModel['history']
    ownRiskMetrics?: RiskMetricsModel | null
    /** Границы лота и тейка клиента, что он сохранил, и его сделок в день по факту. */
    tpMin: number
    tpMax: number
    tpStep: number
    lotMin: number
    lotMax: number
    lotStep: number
    clientLot?: number | null
    clientTakeProfit?: number | null
    ownTradesPerDay?: number | null
}

export interface FeeTierModel {
    fromContracts: number
    fee: number
}

/** Калькулятор робота Rithmic (фьючерсы): цена, маржа брокера, правила доливки. */
export interface FuturesCalculatorDataModel {
    symbolId: number
    symbol: string
    name: string
    contract: string | null
    price: number | null
    /** live — от работающего робота, deal — цена последней сделки. */
    priceSource: 'live' | 'deal' | null
    currency: string
    pointValue: number
    marginDay: number
    marginOvernight: number
    feePerLot: number
    /** Ступени комиссии брокера: с fromContracts контрактов в месяц — fee за контракт. */
    feeTiers: FeeTierModel[]
    takeProfit: number
    positionSize: number
    maxLots: number
    stairPoint: number
    stairFreeZone: number
    stairThreshold: number
    stairThresholdMultiplier: number
    minBalance: number
    compoundStep: number | null
    maxEntryLots: number | null
    balance: number | null
    history: CryptoCalculatorDataModel['history']
    riskMetrics: RiskMetricsModel | null
    /** «Мой счёт»: только сделки счёта, который открыл калькулятор. */
    ownHistory?: CryptoCalculatorDataModel['history']
    ownRiskMetrics?: RiskMetricsModel | null
    /** Границы лота и тейка клиента, что он сохранил, и его сделок в день по факту. */
    tpMin: number
    tpMax: number
    tpStep: number
    lotMin: number
    lotMax: number
    lotStep: number
    clientLot?: number | null
    clientTakeProfit?: number | null
    ownTradesPerDay?: number | null
}

/** Калькулятор на главной: публичные инструменты по биржам. */
export interface PublicRobotsModel {
    rithmic: FuturesCalculatorDataModel[]
    bybit: CryptoCalculatorDataModel[]
    okx: CryptoCalculatorDataModel[]
    bitget: CryptoCalculatorDataModel[]
    binance: CryptoCalculatorDataModel[]
}

/** Лот и тейк клиента — сохранить (пусто — как у инструмента). */
export interface ClientSettingsModel {
    symbolId: number
    clientLot: number | null
    clientTakeProfit: number | null
}

export interface ClientSettingsResultModel {
    clientLot: number | null
    clientTakeProfit: number | null
    robotRunning: boolean
}

export interface RiskMetricsModel {
    days: number
    accounts: number
    windowDays: number
    fromDay: string
    toDay: string
    sharpe?: number | null
    sortino?: number | null
    volatility?: number | null
    maxDrawdown?: number | null
    annualReturn?: number | null
    calmar?: number | null
    bestDay?: number | null
    worstDay?: number | null
    /** equity — по снимкам капитала; deals/mixed — часть дней по закрытым сделкам. */
    source?: 'equity' | 'deals' | 'mixed'
    dealDays?: number
    /** С какого числа дней истории показатели считаются. */
    minDays?: number
    /** Факт за период (без пересчёта на год), доля. */
    periodReturn?: number | null
    winDays?: number | null
    trades?: number
    winRate?: number | null
    /** null — убыточных сделок не было (бесконечность). */
    profitFactor?: number | null
    grossLoss?: number
    /** Просадка открытой позиции — по снимкам капитала (баланс + плавающий результат). */
    equityDays?: number
    equityMaxDrawdown?: number | null
    equityWorstDay?: number | null
    equityFromDay?: string | null
    equityToDay?: string | null
}

export interface WithdrawStatusRequestModel {
    isWithdraw: boolean
}

export interface ChangeRobotParamsModel {
    candleCount?: number
}

export interface BinanceSymbolsGetMinTokenBalanceRequestModel {
    symbol_id: number
}

export interface BybitSearchDto {
    isRun: boolean
}

export interface BybitSymbolBaseDto {
    name: string
    symbol: string
    stepSize: number
    defaultPositionSize: number
    defaultMaxLots: number
    // Глубина просадки (%), на которой доливки набирают позицию до MAX.
    fullDrawdown: number
    // Версия алгоритма доливки: safe | gap_protect | fibonacci | classic.
    stairVersion: string
    // Лимит размера одной доливки для classic: не больше MAX_LOTS/N.
    classicMaxSteps: number
    minBalance: number
    takeProfitRatio: number
    feedCount: number
    onlyAdmin: boolean
    priceStep: number
    quantityStep: number
    leverage?: number
    hedgingEnable: boolean
    candleType?: string
    candleVolume?: number
    interval?: string
    hedgeV2Enable?: boolean
    hedgeV2UseLevels?: boolean
    stairTrendThrottle?: boolean
    hedgeV2BandFirstOpen?: boolean
    hedgeV2FlipEnable?: boolean
    /** Границы для лота и тейка клиента; пусто — тейк ½…×5, лот до ×10. */
    clientTpMin?: number | null
    clientTpMax?: number | null
    clientLotMax?: number | null
}

export interface BybitSymbolDto extends BybitSymbolBaseDto {
    id: number
}

export interface BybitSymbolForSelectDto {
    id: number
    name: string
    symbol: string
    onlyAdmin: boolean
    minBalance: number
}

export interface DealsSearchDto {
    accountId: number
    startDate?: number
    endDate?: number
}

export interface DealDto {
    id: number
    createDate: number
    symbol: string
    size: number
    entryPrice: number
    exitPrice: number
    marketCommission: number
    commission: number
    profit: number
}

export interface DealsStatisticSummaryDto {
    today: number
    // Неделя (с понедельника) и последние 5 дней — опциональные, пока
    // отдаёт только Bybit/Rithmic backend; остальные биржи — undefined.
    week?: number
    fiveDays?: number
    month: number
    year: number
    // Сумма комиссий за тот же период.  RCC = наша платформенная комиссия,
    // USDT = биржевая.  Опциональные ради backward-compat — у роботов где
    // backend ещё не обновлён, поля будут undefined.
    todayCommissionRcc?: number
    weekCommissionRcc?: number
    fiveDaysCommissionRcc?: number
    monthCommissionRcc?: number
    yearCommissionRcc?: number
    todayCommissionUsdt?: number
    weekCommissionUsdt?: number
    fiveDaysCommissionUsdt?: number
    monthCommissionUsdt?: number
    yearCommissionUsdt?: number
}

export interface DealsPeriodStatisticDto {
    startDate: number
    endDate?: number
}

// Ответ от /<robot>-deals/period/{accountId} — теперь возвращает не голое
// число (profit), а объект с тремя агрегатами: profit и две комиссии.
export interface DealsPeriodStatisticResultDto {
    profit: number
    commissionRcc: number
    commissionUsdt: number
}

export interface MinTokenBalanceDto {
    marketId: number
    symbolId: number
}

export interface RithmicSymbolForSelectDto {
    id: number
    name: string
    symbol: string
}

export interface RithmicNativeSymbolDto {
    items: string[]
}

/** Один живой контракт под корневым символом (см. GET /rithmic-symbols/contract-months/{root}) */
export interface RithmicContractMonthDto {
    symbol: string
    expirationDate: string
    isCurrent: boolean
}

export enum RithmicCandleType {
    TICK = 'TICK_BAR',
    SECOND = 'SECOND_BAR',
}

export interface RithmicSymbolBaseDto {
    name: string
    symbol: string
    stepSize: number
    defaultPositionSize: number
    defaultMaxLots: number
    // Устаревшие поля версий доливки (для Rithmic не используются).
    fullDrawdown?: number
    stairVersion?: string
    classicMaxSteps?: number
    // Пороги доливки после 20% от максимума лотов: порог (пунктов) и
    // множитель порога после каждой доливки (по умолчанию 2 → 4 → 8).
    stairThreshold: number
    stairThresholdMultiplier: number
    // Вход в позицию: как раньше или по регрессионному каналу.
    entryMode: 'channel_mid' | 'regression'
    regressionPeriod: number
    regressionDeviation: number
    // Сложный процент лотами: рекомендованный депозит = minBalance × 3,75,
    // +1 лот на каждые compoundStep $ роста, не больше maxEntryLots лотов входа.
    minBalance?: number
    compoundStep?: number | null
    maxEntryLots?: number | null
    /** Ступени комиссии брокера по объёму в месяц. */
    feeTiers?: FeeTierModel[] | null
    /** Границы для лота и тейка клиента; пусто — тейк ½…×5, лот до ×10. */
    clientTpMin?: number | null
    clientTpMax?: number | null
    clientLotMax?: number | null
    takeProfitRatio: number
    marketFee: number
    onlyAdmin: boolean
    candleType: RithmicCandleType
    candleVolume: number
    marketHedgingFee?: number
    hedgingSymbol?: string
    hedgingPositionSize?: number
}

export interface RithmicSymbolDto extends RithmicSymbolBaseDto {
    id: number
}

// ─── Trading Technologies (TT) ───────────────────────────────────────────────

export interface TTCreateModel {
    appKey: string
    appSecret: string
    isSim: boolean
}

export interface TTUpdateDto {
    appKey?: string
    appSecret?: string
    ttAccountId?: string
    isSim?: boolean
    commissionRatio?: number
    isBeStopped?: boolean
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface TTDataModel {
    id: number
    createDate: number
    appKey?: string
    ttAccountId?: string | null
    isSim: boolean
    commissionRatio: number
    isBeStopped: boolean
    robotId?: string | null
    symbolId?: number | null
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
}

export interface TTDataSearchDto extends TTDataModel {
    account: AccountModel
    symbol?: TTSymbolDto | null
}

export interface TTStartDto {
    symbolId: number
}

export interface TTSymbolBaseDto {
    name: string
    symbol: string
    productType?: string
    stepSize: number
    defaultPositionSize: number
    defaultMaxLots: number
    fullDrawdown: number
    stairVersion: string
    classicMaxSteps: number
    takeProfitRatio: number
    marketFee: number
    onlyAdmin: boolean
    minBalance?: number
}

export interface TTSymbolDto extends TTSymbolBaseDto {
    id: number
}

export interface TTSymbolForSelectDto {
    id: number
    name: string
    symbol: string
}

// ─── CQG WebAPI ──────────────────────────────────────────────────────────────

export interface CQGCreateModel {
    username: string
    password: string
    isSim: boolean
}

export interface CQGUpdateDto {
    username?: string
    password?: string
    cqgAccountId?: string
    isSim?: boolean
    commissionRatio?: number
    isBeStopped?: boolean
    hedgeDrawdownRatio?: number
    hedgeMinProfitXCommission?: number
    hedgeTakeallRatio?: number
}

export interface CQGDataModel {
    id: number
    createDate: number
    username?: string
    cqgAccountId?: string | null
    isSim: boolean
    commissionRatio: number
    isBeStopped: boolean
    robotId?: string | null
    symbolId?: number | null
    hedgeDrawdownRatio: number
    hedgeMinProfitXCommission: number
    hedgeTakeallRatio: number
}

export interface CQGDataSearchDto extends CQGDataModel {
    account: AccountModel
    symbol?: CQGSymbolDto | null
}

export interface CQGStartDto {
    symbolId: number
}

export interface CQGSymbolBaseDto {
    name: string
    symbol: string
    productType?: string
    stepSize: number
    defaultPositionSize: number
    defaultMaxLots: number
    fullDrawdown: number
    stairVersion: string
    classicMaxSteps: number
    takeProfitRatio: number
    marketFee: number
    onlyAdmin: boolean
    minBalance?: number
}

export interface CQGSymbolDto extends CQGSymbolBaseDto {
    id: number
}

export interface CQGSymbolForSelectDto {
    id: number
    name: string
    symbol: string
}

/** Отзыв на главной странице */
export interface CommentModel {
    id: number
    createDate: number
    /** Текст на языке сайта: перевод, если он был, иначе оригинал */
    text: string
    /** Исходный текст — приходит только когда показан перевод */
    originalText?: string | null
    isTranslated: boolean
    authorId: number
    authorName: string
    /** Может ли текущий пользователь удалить этот отзыв (свой / админ) */
    canDelete: boolean
    /** Сколько лайков собрал отзыв */
    likes: number
    /** Лайкнул ли его текущий читатель */
    isLiked: boolean
}

/** Итог по бирже в публичном отчёте */
export interface ExchangeSummaryModel {
    exchange: string
    dealsCount: number
    accountsCount: number
    profit: number
    marketCommission: number
    winRate: number
    firstDealDate: number | null
    lastDealDate: number | null
}

/** Итог по одному (анонимному) счёту */
export interface AccountSummaryModel {
    accountId: number
    label: string
    dealsCount: number
    profit: number
    marketCommission: number
    winRate: number
    firstDealDate: number | null
    lastDealDate: number | null
    /** Последний известный депозит счёта; null — если баланс не записан */
    deposit: number | null
    /** Доходность к депозиту, % */
    roi: number | null
}

/** Строка сделки в публичном отчёте */
export interface PublicDealModel {
    createDate: number
    accountId: number
    symbol: string
    size: number
    entryPrice: number
    exitPrice: number
    marketCommission: number
    profit: number
}

/** Точка помесячного графика доходности */
export interface MonthlyPointModel {
    month: string
    dealsCount: number
    profit: number
    marketCommission: number
    /** Средний депозит за месяц; null — если баланс тогда не записывался */
    deposit: number | null
    /** Доходность за месяц к депозиту, % */
    roi: number | null
}

/** Свой инструмент клиента (backend shared/symbol_proposals.py). */
export type ProposalExchange = 'bybit' | 'okx' | 'bitget' | 'binance' | 'rithmic'
export type ProposalStatus = 'pending' | 'active' | 'rejected'

export interface ProposalCandidateModel {
    symbol: string
    priceStep: number
    quantityStep: number
    minQty: number
    ctVal?: number | null
    exists: boolean
}

export interface ProposalBodyModel {
    symbol: string
    lot: number
    takeProfit: number
}

export interface ProposalPreviewModel {
    /** Поля инструмента, как в админке (snake_case). */
    params: Record<string, any>
    info: {
        templates: string[]
        recommendedTakeProfit: number | null
        notional: number | null
        volatility30: number | null
        volatilityFactor: number
        price: number | null
        lot: number
        takeProfit: number
    }
    warnings: string[]
}

export interface ProposalCreatedModel {
    id: number
    name: string
    status: ProposalStatus
    warnings: string[]
}

export interface ProposalRowModel {
    exchange: ProposalExchange
    id: number
    name: string
    symbol: string
    status: ProposalStatus
    reviewNote: string | null
    createDate: number
    lot: number | null
    takeProfit: number | null
    recommendedTakeProfit: number | null
    templates: string[]
    warnings: string[]
    ownerId: number | null
    owner: string | null
    stepSize: number | null
    maxLots: number | null
    minBalance: number | null
}
